import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useWorkspacePermissions } from "@/hooks/use-workspace-permissions";
import { useDeleteKnowledgeBase, useKbStores, useKbUsage } from "@/hooks/use-knowledge-base";
import { failureMessage, type KnowledgeBase } from "@/services/knowledge-base.service";
import { CreateKnowledgeBaseDialog } from "./CreateKnowledgeBaseDialog";
import { KnowledgeBaseCard } from "./KnowledgeBaseCard";
import { UsageStrip } from "./UsageStrip";

interface KnowledgeBaseListProps {
  workspaceId: string;
  onOpen: (id: string) => void;
}

/** The workspace's knowledge bases: create (owner, admin, developer), open, delete (owner, admin). */
export function KnowledgeBaseList({ workspaceId, onOpen }: KnowledgeBaseListProps) {
  const { canEdit, canDelete } = useWorkspacePermissions();
  const stores = useKbStores(workspaceId);
  const usage = useKbUsage(workspaceId);
  const remove = useDeleteKnowledgeBase(workspaceId);
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState<KnowledgeBase | null>(null);
  const [deleteError, setDeleteError] = useState<string>();

  const confirmDelete = async () => {
    setDeleteError(undefined);
    try {
      await remove.mutateAsync(deleting!.id);
      toast({ title: "Knowledge base deleted", description: `"${deleting!.display_name}" and its files were deleted.` });
      setDeleting(null);
    } catch (error) {
      setDeleteError(failureMessage(error, "The knowledge base could not be deleted. Try again in a moment."));
    }
  };

  const term = search.trim().toLowerCase();
  const shown = stores.data?.filter((kb) => kb.display_name.toLowerCase().includes(term));

  return (
    <div className="flex h-full flex-col overflow-y-auto p-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="mb-2 text-3xl font-bold tracking-tight">Knowledge Base</h1>
          <p className="text-muted-foreground">Upload documents your agents search during calls.</p>
        </div>
        {canEdit && <CreateKnowledgeBaseDialog workspaceId={workspaceId} onCreated={(kb) => onOpen(kb.id)} />}
      </div>

      {stores.isPending || usage.isPending ? (
        <div role="status" aria-label="Loading knowledge bases" className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      ) : stores.isError || usage.isError ? (
        <div role="alert" className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed py-12">
          <p className="mb-4 text-muted-foreground">The knowledge bases could not be loaded.</p>
          <Button
            variant="outline"
            onClick={() => {
              void stores.refetch();
              void usage.refetch();
            }}
          >
            Try again
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <UsageStrip usage={usage.data} />
          {stores.data.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-muted/30 py-12 text-center text-muted-foreground">
              No knowledge bases yet.
              {canEdit && " Create one, then upload the documents your agents should know."}
            </div>
          ) : (
            <>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  aria-label="Search knowledge bases"
                  placeholder="Search knowledge bases..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="pl-10"
                />
              </div>
              {shown.length === 0 ? (
                <p className="py-12 text-center text-muted-foreground">No knowledge base matches your search.</p>
              ) : (
                <div className="grid grid-cols-1 gap-6 pb-6 md:grid-cols-2 lg:grid-cols-3">
                  {shown.map((kb) => (
                    <KnowledgeBaseCard
                      key={kb.id}
                      kb={kb}
                      maxFiles={usage.data.limits.max_files_per_kb}
                      canDelete={canDelete}
                      onOpen={() => onOpen(kb.id)}
                      onDelete={() => {
                        setDeleteError(undefined);
                        setDeleting(kb);
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* A delete in flight finishes in view; a refusal stays on screen with it. */}
      <AlertDialog open={!!deleting} onOpenChange={() => !remove.isPending && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Its files and their text are deleted too, and agents can no longer search it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <p role="alert" className="text-sm text-destructive">
              {deleteError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending} aria-busy={remove.isPending}>
              {remove.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
